import { Bot, PhoneCall } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { TabsContent } from "@/shared/components/ui/tabs";
import { Button } from "@/shared/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { displayValue } from "@/modules/hrPortal/utils/formatters";
import { getVoiceCallStatusColor } from "@/modules/hrPortal/utils/voiceCallHelpers";
import type { VoiceCallLog } from "@/modules/hrPortal/types";
import InterestStars from "@/modules/hrPortal/components/shared/InterestStars";

interface VoiceCallsTabProps {
  dashboardStats: {
    voiceCallSuccessRate?: number | null;
  };
  voiceCallLogs: VoiceCallLog[];
  onStartCampaign: () => void;
  onCallSelect: (call: VoiceCallLog) => void;
}

export default function VoiceCallsTab({
  dashboardStats,
  voiceCallLogs,
  onStartCampaign,
  onCallSelect,
}: VoiceCallsTabProps) {
  return (
    <TabsContent value="voice-calls">
      <div className="space-y-6">
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Bot className="h-6 w-6 text-green-600" />
                  <h3 className="font-semibold">AI Voice Agent</h3>
                  <Badge className="bg-green-500">Active</Badge>
                </div>
                <p className="text-sm text-green-700">Ready to make calls</p>
              </div>

              <div className="flex items-center gap-4">
                <div className="px-4 text-center">
                  <p className="text-2xl font-bold">
                    {displayValue(dashboardStats.voiceCallSuccessRate)}%
                  </p>
                  <p className="text-xs text-muted-foreground">Success Rate</p>
                </div>

                <Button onClick={onStartCampaign}>
                  Start Voice Campaign
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Call History</CardTitle>
            <CardDescription>
              AI voice agent call logs and recordings
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Interest</TableHead>
                    <TableHead>Insights</TableHead>
                    <TableHead>Time</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {voiceCallLogs.map((call) => (
                    <TableRow
                      key={call.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => onCallSelect(call)}
                    >
                      <TableCell className="font-medium">
                        {call.candidateName}
                      </TableCell>

                      <TableCell>{call.phone}</TableCell>

                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={getVoiceCallStatusColor(call.status ?? "")}
                        >
                          {call.status}
                        </Badge>
                      </TableCell>

                      <TableCell>{call.duration}</TableCell>

                      <TableCell>
                        {call.interestLevel && call.interestLevel > 0 ? (
                          <InterestStars level={call.interestLevel} />
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      <TableCell>
                        <div className="max-w-[200px] truncate text-sm text-muted-foreground">
                          {call.insights?.join(", ")}
                        </div>
                      </TableCell>

                      <TableCell className="text-muted-foreground">
                        {call.timestamp}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </TabsContent>
  );
}
